import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Container, Row, Col } from "react-bootstrap";
import { css } from "emotion";

import Translate from "app/universal/utils/Translate";
import UrlQuotation from "app/universal/utils/UrlQuotation";
import UpdateDataApi from "app/universal/utils/UpdateDataApi";
import LeMoulinApi from "app/universal/utils/LeMoulinApi";
import EditableNumbering from './EditableNumbering';
import { isUnusualRecoveryDate } from "helper/globalHelper";

const Delivery = (props) => {
  const {
    delivery,
    colorDeliveryMan,
    roadmapName,
    sort,
    filters,
    handler,
    getUrl,
    theme
  } = props;

  const [localNumbering, setLocalNumbering] = useState(delivery.info && delivery.info.numbering);
  const orderPictos = Array.isArray(delivery?.info?.pictos) ? delivery.info.pictos : [];
  const getPictoUrl = (pictoUrl) => {
    if (!pictoUrl) {
      return "";
    }
    if (pictoUrl.startsWith("http://") || pictoUrl.startsWith("https://")) {
      return pictoUrl;
    }
    return `${process.env.REACT_APP_API_URL}${pictoUrl}`;
  };

  const styles = {
    delivery: {
      background: "white",
      margin: "10px 0",
      transition: "300ms",
      position: "relative",
      border: "1px #dedede solid",
      ":before": {
        content: '""', // Correction des guillemets
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: "5px",
        height: "100%",
        background: delivery?.delivery_done ? "green" : "red",
      }
    },
    statut: {
      textTransform: "uppercase",
      fontSize: "0.6rem",
      "@media print": {
        fontSize: "1.4rem",
      },
      marginBottom: "0rem",
    },
    col: {
      padding: "0.5rem",
      borderRight: "1px #dedede solid",
      display: "flex",
      justifyContent: "center",
      flexDirection: "column",
    },
    verticalAlign: {
      display: "flex",
      flexDirection: "row",
      position: "relative"
    },
    para: {
      marginBottom: "0rem",
      fontSize: "0.8rem",
      lineHeight: "1.3",
      "@media print": {
        fontSize: "1.6rem",
      },
      "@media (max-width: 767px)": {
        textAlign: 'left'
      },
    },
    paraCom: {
      marginLeft: "0.5rem",
      marginBottom: "0rem",
      fontSize: "0.8rem",
      "@media print": {
        fontSize: "1.4rem",
      },
    },
    center: {
      textAlign: "center",
    },
    noImg: {
      backgroundColor: "#dedede",
      textAlign: "center",
      color: "#ccc",
      wordBreak: "break-word",
    },
    img: {
      backgroundSize: "cover",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
      minHeight: "100px",
      textAlign: "center",
    },
    divisor: {
      height: "1px",
      backgroundColor: "#dedede",
      margin: "1.0rem 0",
    },
    borderRight: {
      "@media (max-width: 767px)": {
        borderRight: "none",
      },
    },
    mobileDivisor: {
      "@media (max-width: 767px)": {
        display: "none",
      },
    },
    blockColor: {
      width: "50px",
      borderRadius: "4px",
      marginBottom: "0.5rem",
      color: "white",
      textShadow: "1px 1px 2px grey",
      textAlign: "center",
    },
    blockColorPrint: {
      height: "25px",
      position: "absolute",
      top: "0",
      right: "0",
      color: "white",
      textShadow: "1px 1px 2px grey",
      textAlign: "center",
      "@media print": {
        fontSize: "2.2rem",
      },
    },
    comment: {
      borderTop: "1px #dedede solid",
      fontSize: "0.8rem",
      "@media print": {
        fontSize: "1.6rem",
      },
    },
    company: {
      textTransform: "uppercase",
      color: "#429853",
      fontWeight: "bold",
      fontSize: "0.8rem",
      "@media print": {
        fontSize: "1.6rem",
      },
    },
    yes: {
      backgroundColor: 'red',
      color: 'white',
      padding: '5px'
    },
    no: {
      backgroundColor: 'grey',
      color: 'white',
      padding: '5px'
    },
    pictosWrapper: {
      display: "flex",
      flexWrap: "wrap",
      gap: "6px",
      marginBottom: "0.5rem",
      alignItems: "center"
    },
    picto: {
      width: "28px",
      height: "28px",
      objectFit: "contain",
      borderRadius: "4px"
    },
    recoveryDateUnusual: {
      background: "#fff3cd",
      border: "1px solid #ffc107",
      borderRadius: "4px",
      padding: "6px 8px",
      color: "#856404",
      marginBottom: "0.5rem",
    },
    recoveryDateUnusualHint: {
      fontSize: "0.75rem",
      fontWeight: "bold",
      marginTop: "4px",
      marginBottom: 0,
    },
  };

  const hasUnusualRecoveryDate = delivery.recovery_date
    && isUnusualRecoveryDate(delivery.delivery_date, delivery.recovery_date);

  useEffect(() => {
    setLocalNumbering(delivery?.info?.numbering);
  }, [delivery?.id, delivery?.info?.numbering]);

  return (
    <div>
      {theme === "light" ? (
        <Container
          className={css(styles.delivery, {
            border: filters.showDelivery === delivery.id && "3px green solid",
          })}
          name={"delivery-" + delivery.id}
          fluid
        >
          <Row className={css(styles.verticalAlign)}>
            <Col xs={12} md={12} className={css(styles.col)}>
              <EditableNumbering
                numbering={localNumbering}
                orderId={delivery.info && delivery.info.order_id}
                backgroundColor={colorDeliveryMan ? colorDeliveryMan : 'grey'}
                onChange={val => setLocalNumbering(val)}
              />
              {orderPictos.length > 0 && (
                <div className={css(styles.pictosWrapper)}>
                  {orderPictos.map((picto) => (
                    <img
                      key={picto.url}
                      src={getPictoUrl(picto.url)}
                      alt={picto.name || "Picto prestation"}
                      title={picto.name || "Picto prestation"}
                      className={css(styles.picto)}
                    />
                  ))}
                </div>
              )}
              {delivery?.distance !== undefined && 
                <span className={css(styles.para)}>
                  Distance: {delivery.distance / 1000} km
                </span>
              }
              {delivery?.travelDuration !== undefined && 
                <span className={css(styles.para)}>
                  Temps de conduire de la tournée en cours: {delivery.travelDuration} min
                </span>
              }
              {delivery?.arrivalTime && 
                <span className={css(styles.para)}>
                  Arrivée estimée: {moment(delivery.arrivalTime).format("HH:mm")}
                </span>
              }
              {delivery?.setupTime !== undefined && 
                <span className={css(styles.para)}>
                  Temps installation voiture: {delivery.setupTime} min
                </span>
              }
              {delivery?.installationTime && 
                <span className={css(styles.para)}>
                  Temps d'installation: {Math.round(delivery.installationTime)} min
                </span>
              }
              {delivery?.departureTime && 
                <span className={css(styles.para)}>
                  Départ estimé: {moment(delivery.departureTime).format("HH:mm")}
                </span>
              }
              <UrlQuotation url={delivery.info.quotation_url} sheetRoad={`/feuille-de-route/${delivery.info.order_id}`} info={delivery.info} prestation={delivery.prestation_type} date={delivery.delivery_date}/>
              
              <h3 className={css(styles.statut)}>
                {!delivery.info.grouped_order_id &&
                  <p className="text-center" style={{ color: "red"}}>Ancien système1, merci de migrer cette commande sur le nouveau système</p> 
                }
                Livraison <Translate item={delivery.prestation_type} /> -{" "}
                <b>
                  {delivery.firstname ? delivery.firstname : "Prénom : NC, "} {delivery.lastname ? delivery.lastname : "Nom : NC"} - {delivery.person}{" "}
                  pers
                </b>
              </h3>
              <p className={css(styles.para)}>
                {delivery?.info?.is_tender && (
                  <small style={{color: 'black', backgroundColor: '#f5baed', padding: '2px 4px', borderRadius: '4px', marginRight: '4px'}}>AO</small>
                )}
                <span className={css(styles.company)}>{delivery.company}</span>
                <br />
                {delivery.street_number} {delivery.street ? delivery.street : "Rue : NC, "}{" "}
                {delivery.postal_code ? delivery.postal_code : "Code postal :NC, "} {delivery.city ? delivery.city : "Ville : NC"}
                <br />
                <i>{delivery.address_complement}</i>
                <br />
                <br />
              </p>
              <Row noGutters>
                <Col xs={12} md={4}>
                  <p className={css(styles.para)}>Arrivé entre :</p>
                </Col>
                <Col xs={12} sm={12} md={4}>
                  <UpdateDataApi
                    value={delivery.arrived_at_start}
                    id={delivery.id}
                    name={"arrived_at_start"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <UpdateDataApi
                    value={delivery.arrived_at_end}
                    id={delivery.id}
                    name={"arrived_at_end"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
              </Row>
              <Row noGutters>
                <Col xs={12} md={4}>
                  <p className={css(styles.para)}>Début:</p>
                </Col>
                <Col xs={12} md={4}>
                  <UpdateDataApi
                    value={delivery.start_at}
                    id={delivery.id}
                    name={"start_at"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
              </Row>
              {delivery.recovery_date && (
                <Row noGutters>
                  <Col xs={12} md={4}>
                    <p className={css(styles.para)}>Récup. prévue :</p>
                  </Col>
                  <Col xs={12} md={8}>
                    <p className={css(styles.para, hasUnusualRecoveryDate && styles.recoveryDateUnusual)}>
                      <b>{moment(delivery.recovery_date).format("DD/MM/YYYY")}</b>
                      {hasUnusualRecoveryDate && (
                        <>
                          <br />
                          <span className={css(styles.recoveryDateUnusualHint)}>
                            Livraison le {moment(delivery.delivery_date).format("DD/MM/YYYY")} — pas le lendemain
                          </span>
                        </>
                      )}
                      {(delivery.recovery_start_at || delivery.recovery_end_at) && (
                        <>
                          <br />
                          {delivery.recovery_start_at ? moment(delivery.recovery_start_at).format("H:mm") : "?"}
                          {" – "}
                          {delivery.recovery_end_at ? moment(delivery.recovery_end_at).format("H:mm") : "?"}
                        </>
                      )}
                    </p>
                  </Col>
                </Row>
              )}
              {/* <Row noGutters>
                <Col xs={12} md={4}>
                  <p className={css(styles.para)}>Livraison faite :</p>
                </Col>
                <UpdateDataApi
                  label={""}
                  value={delivery.delivery_done}
                  id={delivery.id}
                  name={"deliveryDone"}
                  api={"delivery"}
                  input={"checkbox"}
                  handler={handler}
                />
              </Row> */}
            </Col>
            <Col xs={12} className={css(styles.col, styles.comment)}>
              {delivery.more_information && (
                <p className={css(styles.paraCom)}>
                  <b>Commentaire Client :</b> {delivery.more_information}
                </p>
              )}
              <UpdateDataApi
                value={delivery.delivery_information}
                id={delivery.id}
                name={"delivery_information"}
                api={"delivery"}
                input={"text"}
                placeholder={"Ajouter un commentaire..."}
                handler={handler}
              />
            </Col>
          </Row>
        </Container>
      ) : theme === "print" ? (
        <Container className={css(styles.delivery)} fluid>
          
          <Row className={css(styles.verticalAlign)}>
            <div
                className={css(styles.blockColorPrint, {
                  backgroundColor: (colorDeliveryMan ? colorDeliveryMan : 'grey'),
                })}
              >
                {delivery.info && delivery.info.numbering ? delivery.info.numbering : roadmapName && roadmapName + sort}
              </div>
            <Col xs={12} md={6} className={css(styles.col)}>
              <h3 className={css(styles.statut)}>Livraison</h3>
              <p className={css(styles.para)}>
                {delivery?.info?.is_tender && (
                  <small style={{color: 'black', backgroundColor: '#f5baed', padding: '2px 4px', borderRadius: '4px', marginRight: '4px'}}>AO</small>
                )}
                <span className={css(styles.company)}>{delivery.company}</span>
                <br />
                {delivery.street_number} {delivery.street ? delivery.street : "Rue : NC, "}{" "}
                {delivery.postal_code ? delivery.postal_code : "Code postal :NC, "} {delivery.city ? delivery.city : "Ville : NC"}
                <br />
                <i>{delivery.address_complement}</i>
                <br />
                <i>
                  {delivery.firstname ? delivery.firstname : "Prénom : NC, "} {delivery.lastname ? delivery.lastname : "Nom : NC"} / <a href={`tel:${delivery.phone}`}>{delivery.phone}</a> / {delivery.phone_secondary && <a href={`tel:${delivery.phone_secondary}`}>{delivery.phone_secondary}</a>}
                  <br />
                </i>
              </p>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              <Row noGutters>
                <Col xs={12} md={12} className={css(styles.borderRight)}>
                  <p className={css(styles.para, styles.center)}>
                    <Translate item={delivery.prestation_type} />
                  </p>
                  <p className={css(styles.para, styles.center)}>
                    {delivery.person} personne(s)
                  </p>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              <Row noGutters>
                <Col xs={12} md={12}>
                  <p className={css(styles.para, styles.center)}>
                    Livr entre:{" "}
                    {delivery.arrived_at_start
                      ? moment(delivery.arrived_at_start).format("H:mm")
                      : "NC"}{" "}
                    et{" "}
                    {delivery.arrived_at_start
                      ? moment(delivery.arrived_at_end).format("H:mm")
                      : "NC"}
                  </p>
                </Col>
              </Row>
              <Row noGutters>
                <Col xs={12} md={12}>
                  <p className={css(styles.para, styles.center)}>
                    Début:
                    {delivery.start_at
                      ? moment(delivery.start_at).format("H:mm")
                      : "NC"}{" "}
                  </p>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              {delivery.blanket ? (
                <p className={css(styles.para, styles.center)}>Nappe</p>
              ) : (
                <p className={css(styles.para, styles.center)}>Pas de nappe</p>
              )}
            </Col>
            {delivery.more_information && (
              <Col xs={12} className={css(styles.col, styles.comment)}>
                Commentaire Client : {delivery.more_information}
              </Col>
            )}
            {delivery.delivery_information && (
              <Col xs={12} className={css(styles.col, styles.comment)}>
                Commentaire interne livraison : {delivery.delivery_information}
              </Col>
            )}
          </Row>
        </Container>
      ) : (
        <Container
          className={css(styles.delivery, {
            border: filters.showDelivery === delivery.id && "3px green solid",
          })}
          name={"delivery-" + delivery.id}
          fluid
        >
          <Row className={css(styles.verticalAlign)}>
            <Col xs={12} md={6} className={css(styles.col)}>
              <EditableNumbering
                numbering={localNumbering}
                orderId={delivery.info && delivery.info.order_id}
                backgroundColor={colorDeliveryMan ? colorDeliveryMan : 'grey'}
                onChange={val => setLocalNumbering(val)}
              />
              {orderPictos.length > 0 && (
                <div className={css(styles.pictosWrapper)}>
                  {orderPictos.map((picto) => (
                    <img
                      key={picto.url}
                      src={getPictoUrl(picto.url)}
                      alt={picto.name || "Picto prestation"}
                      title={picto.name || "Picto prestation"}
                      className={css(styles.picto)}
                    />
                  ))}
                </div>
              )}
              <UrlQuotation url={delivery.info.quotation_url} sheetRoad={`/feuille-de-route/${delivery.info.order_id}`} info={delivery.info} prestation={delivery.prestation_type} date={delivery.delivery_date}/>
              <h3 className={css(styles.statut)}>Livraison</h3>
              <p className={css(styles.para)}>
                {delivery?.info?.is_tender && (
                  <small style={{color: 'black', backgroundColor: '#f5baed', padding: '2px 4px', borderRadius: '4px', marginRight: '4px'}}>AO</small>
                )}
                <span className={css(styles.company)}>{delivery.company}</span>
                <br />
                {delivery.street_number} {delivery.street ? delivery.street : "Rue : NC, "}{" "}
                {delivery.postal_code ? delivery.postal_code : "Code postal :NC, "} {delivery.city ? delivery.city : "Ville : NC"}
                <br />
                <i>{delivery.address_complement}</i>
                <br />
                <br />
                <b>
                  {delivery.firstname ? delivery.firstname : "Prénom : NC, "} {delivery.lastname ? delivery.lastname : "Nom : NC"}
                </b>
                <br />
                {delivery.phone}
              </p>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              <Row noGutters>
                <Col xs={6} md={12} className={css(styles.borderRight)}>
                  <p className={css(styles.para, styles.center)}>
                    <Translate item={delivery.prestation_type} />
                  </p>
                  <div
                    className={css(styles.divisor, styles.mobileDivisor)}
                  ></div>
                </Col>
                <Col xs={6} md={12}>
                  <p className={css(styles.para, styles.center)}>
                    {delivery.person}
                  </p>
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              <Row noGutters>
                <Col xs={12} md={12}>
                  <p className={css(styles.para, styles.center)}>Livr entre:</p>
                </Col>
                <Col xs={12} sm={12} md={6}>
                  <UpdateDataApi
                    value={delivery.arrived_at_start}
                    id={delivery.id}
                    name={"arrived_at_start"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <UpdateDataApi
                    value={delivery.arrived_at_end}
                    id={delivery.id}
                    name={"arrived_at_end"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
              </Row>
              <div className={css(styles.divisor)}></div>
              <Row noGutters>
                <Col xs={12} md={6}>
                  <p className={css(styles.para, styles.center)}>Début:</p>
                </Col>
                <Col xs={12} md={6}>
                  <UpdateDataApi
                    value={delivery.start_at}
                    id={delivery.id}
                    name={"start_at"}
                    api={"delivery"}
                    input={"times"}
                    handler={handler}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={12} md={2} className={css(styles.col)}>
              {/* <UpdateDataApi
                label={"Nappe"}
                value={delivery.blanket}
                id={delivery.id}
                name={"blanket"}
                api={"delivery"}
                input={"checkbox"}
                handler={handler}
              /> */}
              {roadmapName &&
                <div style={{ color: delivery?.delivery_done ? "green" : "red"}}>
                  <UpdateDataApi
                    label={"Livraison faite"}
                    value={delivery?.delivery_done || false}
                    id={delivery.id}
                    name={"deliveryDone"}
                    api={"delivery"}
                    input={"checkbox"}
                    handler={handler}
                  />
                </div>
              }
              <div className={css(styles.divisor)}></div>
              <p className={css(styles.center)} style={{marginBottom: "0px"}}>
                <small>Récup à cocher : </small>
               {delivery.info.hasBackCondition ?  <span className={css(styles.yes)}>oui</span> : <span className={css(styles.no)}>non</span>}
              </p>
              <div className={css(styles.divisor)}></div>
              <UpdateDataApi
                label={"A récupérer"}
                value={delivery.recovery}
                id={delivery.id}
                name={"recovery"}
                api={"delivery"}
                input={"checkbox"}
                handler={handler}
              />
              {delivery.recovery_date && (
                <>
                  <div className={css(styles.divisor)}></div>
                  <p
                    className={css(
                      styles.center,
                      hasUnusualRecoveryDate && styles.recoveryDateUnusual
                    )}
                    style={{ marginBottom: "0px", fontSize: "0.85em" }}
                  >
                    <small><b>Récup. prévue :</b></small>
                    <br />
                    <small><b>{moment(delivery.recovery_date).format("DD/MM/YYYY")}</b></small>
                    {hasUnusualRecoveryDate && (
                      <>
                        <br />
                        <small className={css(styles.recoveryDateUnusualHint)}>
                          Livraison le {moment(delivery.delivery_date).format("DD/MM/YYYY")} — pas le lendemain
                        </small>
                      </>
                    )}
                    {(delivery.recovery_start_at || delivery.recovery_end_at) && (
                      <>
                        <br />
                        <small>
                          {delivery.recovery_start_at ? moment(delivery.recovery_start_at).format("H:mm") : "?"}
                          {" – "}
                          {delivery.recovery_end_at ? moment(delivery.recovery_end_at).format("H:mm") : "?"}
                        </small>
                      </>
                    )}
                  </p>
                </>
              )}
            </Col>
            <Col xs={12} className={css(styles.col, styles.comment)}>
              {delivery.more_information && (
                <p className={css(styles.paraCom)}>
                  <b>Commentaire Client :</b> {delivery.more_information}
                </p>
              )}
              <UpdateDataApi
                value={delivery.delivery_information}
                id={delivery.id}
                name={"delivery_information"}
                api={"delivery"}
                input={"text"}
                placeholder={"Ajouter un commentaire..."}
                handler={handler}
              />
            </Col>
          </Row>
        </Container>
      )}
    </div>
  );
};

Delivery.propTypes = {
  delivery: PropTypes.object,
  colorDeliveryMan: PropTypes.string,
  sort: PropTypes.number
};

export default Delivery;
